import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Loader2, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";
import type { HomeySocketStatus } from "@/types/homey";
import { DURATION, EASE_SIGNATURE } from "./motion";

export interface ConnectionBannerProps {
  socketStatus: HomeySocketStatus;
  homeyStatus: "connected" | "disconnected";
  className?: string;
}

type BannerState =
  | { visible: false }
  | {
      visible: true;
      washClass: string;
      icon: React.ReactNode;
      label: string;
    };

function resolveBannerState(
  socketStatus: HomeySocketStatus,
  homeyStatus: "connected" | "disconnected",
): BannerState {
  // Avoid flashing on initial page-load while the socket is still connecting.
  if (socketStatus === "connecting") return { visible: false };
  // All good: socket is open and Homey reports connected.
  if (socketStatus === "open" && homeyStatus === "connected") return { visible: false };

  if (socketStatus === "reconnecting") {
    return {
      visible: true,
      washClass: "bg-domain-climate-wash text-domain-climate-foreground",
      icon: <Loader2 className="size-4 animate-spin" aria-hidden />,
      label: "Reconnecting...",
    };
  }

  if (socketStatus === "offline") {
    return {
      visible: true,
      washClass: "bg-domain-security-wash text-domain-security-foreground",
      icon: <WifiOff className="size-4" aria-hidden />,
      label: "Connection lost",
    };
  }

  // socketStatus === "open" but homeyStatus === "disconnected"
  return {
    visible: true,
    washClass: "bg-domain-security-wash text-domain-security-foreground",
    icon: <WifiOff className="size-4" aria-hidden />,
    label: "Homey unreachable",
  };
}

/**
 * Fixed top-center slim pill shown whenever the WebSocket is degraded or
 * Homey reports it cannot be reached. Hidden during initial "connecting" to
 * avoid a flash on page load, and hidden entirely when all is well.
 */
export function ConnectionBanner({ socketStatus, homeyStatus, className }: ConnectionBannerProps) {
  const reduced = useReducedMotion();
  const state = resolveBannerState(socketStatus, homeyStatus);

  const transition = { duration: DURATION.base, ease: EASE_SIGNATURE };
  const hiddenY = reduced ? 0 : -24;

  return (
    <AnimatePresence>
      {state.visible && (
        <motion.div
          key="connection-banner"
          data-slot="connection-banner"
          role="status"
          aria-live="polite"
          initial={{ y: hiddenY, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: hiddenY, opacity: 0 }}
          transition={transition}
          className={cn(
            "fixed top-3 left-1/2 z-50 -translate-x-1/2",
            "inline-flex items-center gap-2 rounded-full px-4 py-2",
            "text-sm font-medium shadow-tile",
            state.washClass,
            className,
          )}
        >
          {state.icon}
          {state.label}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
